import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import stockMovementService from '../../services/stockMovementService';
import productService from '../../services/productService';
import type { Customer } from '../../types/customer';
import type { RentalContract, CreateRentalContractData, SerialNumber } from '../../types/rental';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light', boxSizing: 'border-box',
};
const inputSm: React.CSSProperties = {
  ...inputStyle, padding: '6px 10px', fontSize: 11,
};
const inputError: React.CSSProperties = { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' };
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};
const labelSm: React.CSSProperties = { ...labelStyle, fontSize: 8, marginBottom: 4 };
const errorMsg: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4,
};
const ghostBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'none',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'linear-gradient(135deg, #d97706, #b45309)',
  border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#1c2033', cursor: 'pointer',
};
const dangerBtn: React.CSSProperties = {
  padding: '6px 14px', background: 'rgba(201,90,90,0.08)',
  border: '1px solid rgba(201,90,90,0.2)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 8, fontWeight: 400,
  letterSpacing: '0.12em', textTransform: 'uppercase', color: '#c95a5a', cursor: 'pointer',
};

interface RentalFormProps {
  contract?: RentalContract;
  onSubmit: (data: CreateRentalContractData) => void;
  onCancel: () => void;
}

interface MachineSelection {
  serialNumberId: string;
  serialNumber?: string;
  productName?: string;
  monthlyRentalFee: number;
  initialBottlesQty: number;
  initialBottlesMl: number;
  bottlesProductId: string;
}

export const RentalForm: React.FC<RentalFormProps> = ({ contract, onSubmit, onCancel }) => {
  const isEditing = !!contract;

  const [formData, setFormData] = useState({
    customerId: contract?.customerId || '',
    startDate: contract?.startDate ? new Date(contract.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    endDate: contract?.endDate ? new Date(contract.endDate).toISOString().split('T')[0] : '',
    monthlyRentalFee: contract?.monthlyRentalFee?.toString() || '0',
    refillServiceFee: contract?.refillServiceFee?.toString() || '0',
    refillIntervalDays: contract?.refillIntervalDays?.toString() || '30',
    autoRenew: contract?.autoRenew || false,
    notes: contract?.notes || '',
    contractDocument: contract?.contractDocument || ''
  });

  const [selectedMachines, setSelectedMachines] = useState<MachineSelection[]>(
    contract?.machines?.map(m => ({
      serialNumberId: m.serialNumberId,
      serialNumber: m.serialNumber?.serialNumber,
      productName: m.serialNumber?.product?.name,
      monthlyRentalFee: m.monthlyRentalFee || 0,
      initialBottlesQty: 0,
      initialBottlesMl: 500,
      bottlesProductId: ''
    })) || []
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: customersData } = useQuery({
    queryKey: ['customers', { limit: 100, status: 'ACTIVE' }],
    queryFn: () => customerService.getCustomers({ limit: 100, status: 'ACTIVE' })
  });

  const { data: serialNumbers, isLoading: serialsLoading, error: serialsError } = useQuery({
    queryKey: ['available-machines'],
    queryFn: () => stockMovementService.getAllAvailableMachines()
  });

  const { data: productsData } = useQuery({
    queryKey: ['products', { itemType: 'INVENTORY', limit: 100 }],
    queryFn: () => productService.getProducts({ itemType: 'INVENTORY', limit: 100 })
  });

  const customers = customersData?.data || [];
  const bottleProducts = productsData?.data || [];

  console.log('🔍 Rental Form Debug:', { serialsLoading, serialsError, serialNumbersCount: serialNumbers?.length, serialNumbers });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAddMachine = (serialNumberId: string) => {
    const serial = serialNumbers?.find((sn: SerialNumber) => sn.id === serialNumberId);
    if (serial && !selectedMachines.find(m => m.serialNumberId === serialNumberId)) {
      setSelectedMachines(prev => [...prev, {
        serialNumberId,
        serialNumber: serial.serialNumber,
        productName: serial.product?.name || 'Unknown Product',
        monthlyRentalFee: parseFloat(formData.monthlyRentalFee) || 0,
        initialBottlesQty: 0,
        initialBottlesMl: 500,
        bottlesProductId: ''
      }]);
    }
  };

  const handleMachinePriceChange = (serialNumberId: string, price: string) => {
    setSelectedMachines(prev => prev.map(m =>
      m.serialNumberId === serialNumberId ? { ...m, monthlyRentalFee: parseFloat(price) || 0 } : m
    ));
  };

  const handleBottleChange = (serialNumberId: string, field: 'initialBottlesQty' | 'initialBottlesMl' | 'bottlesProductId', value: string | number) => {
    setSelectedMachines(prev => prev.map(m =>
      m.serialNumberId === serialNumberId
        ? { ...m, [field]: field === 'bottlesProductId' ? value : (parseInt(value as string) || 0) }
        : m
    ));
  };

  const handleRemoveMachine = (serialNumberId: string) => {
    setSelectedMachines(prev => prev.filter(m => m.serialNumberId !== serialNumberId));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.endDate) <= new Date(formData.startDate))
      newErrors.endDate = 'End date must be after start date';
    if (selectedMachines.length === 0) newErrors.machines = 'At least one machine is required';
    const machineWithoutPrice = selectedMachines.find(m => isNaN(m.monthlyRentalFee) || m.monthlyRentalFee < 0);
    if (machineWithoutPrice) newErrors.machines = 'All machines must have a valid rental fee';
    const intervalDays = parseInt(formData.refillIntervalDays);
    if (isNaN(intervalDays) || intervalDays < 1) newErrors.refillIntervalDays = 'Refill interval must be at least 1 day';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const submitData: CreateRentalContractData = {
      customerId: formData.customerId,
      startDate: formData.startDate,
      endDate: formData.endDate,
      monthlyRentalFee: parseFloat(formData.monthlyRentalFee),
      refillServiceFee: parseFloat(formData.refillServiceFee) || 0,
      refillIntervalDays: parseInt(formData.refillIntervalDays),
      autoRenew: formData.autoRenew,
      notes: formData.notes || undefined,
      contractDocument: formData.contractDocument || undefined,
      machines: selectedMachines.map(m => ({
        serialNumberId: m.serialNumberId,
        monthlyRentalFee: m.monthlyRentalFee,
        initialBottlesQty: m.initialBottlesQty || 0,
        initialBottlesMl: m.initialBottlesMl || 0,
        bottlesProductId: m.bottlesProductId || undefined
      }))
    };
    onSubmit(submitData);
  };

  const totalMonthlyFee = selectedMachines.reduce((sum, machine) => sum + machine.monthlyRentalFee, 0);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.22)',
        borderRadius: 8, padding: 28,
      }}>
        {/* Title */}
        <h2 style={{
          fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 300,
          color: '#1c2033', margin: '0 0 24px',
        }}>
          {isEditing ? 'Edit Rental Contract' : 'New Rental Contract'}
        </h2>

        {/* Customer + Default Fee */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Customer <span style={{ color: '#c95a5a' }}>*</span></label>
            <select
              value={formData.customerId}
              onChange={(e) => handleInputChange('customerId', e.target.value)}
              style={errors.customerId ? inputError : inputStyle}
              disabled={isEditing}
            >
              <option value="">Select Customer</option>
              {customers.map((customer: Customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            {errors.customerId && <p style={errorMsg}>{errors.customerId}</p>}
          </div>

          <div>
            <label style={labelStyle}>Default Monthly Rental Fee (per machine)</label>
            <input
              type="number" step="0.01"
              value={formData.monthlyRentalFee}
              onChange={(e) => handleInputChange('monthlyRentalFee', e.target.value)}
              placeholder="Default price when adding machines"
              style={inputStyle}
            />
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', marginTop: 4 }}>
              This will be used as the default price for each machine you add
            </p>
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Start Date <span style={{ color: '#c95a5a' }}>*</span></label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              style={errors.startDate ? inputError : inputStyle}
            />
            {errors.startDate && <p style={errorMsg}>{errors.startDate}</p>}
          </div>

          <div>
            <label style={labelStyle}>End Date <span style={{ color: '#c95a5a' }}>*</span></label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              style={errors.endDate ? inputError : inputStyle}
            />
            {errors.endDate && <p style={errorMsg}>{errors.endDate}</p>}
          </div>
        </div>

        {/* Refill Fees */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Refill Service Fee (optional)</label>
            <input
              type="number" step="0.01"
              value={formData.refillServiceFee}
              onChange={(e) => handleInputChange('refillServiceFee', e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Refill Interval (days) <span style={{ color: '#c95a5a' }}>*</span></label>
            <input
              type="number"
              value={formData.refillIntervalDays}
              onChange={(e) => handleInputChange('refillIntervalDays', e.target.value)}
              style={errors.refillIntervalDays ? inputError : inputStyle}
            />
            {errors.refillIntervalDays && <p style={errorMsg}>{errors.refillIntervalDays}</p>}
          </div>
        </div>

        {/* Auto Renew */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.autoRenew}
              onChange={(e) => handleInputChange('autoRenew', e.target.checked)}
              style={{ width: 16, height: 16, accentColor: '#d97706' }}
            />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
              Auto-renew contract when it expires
            </span>
          </label>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            placeholder="Add any notes or special terms..."
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
        </div>

        {/* Machine Selection */}
        <div style={{ borderTop: '1px solid rgba(92,78,51,0.15)', paddingTop: 24 }}>
          <h3 style={{
            fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500,
            letterSpacing: '0.25em', textTransform: 'uppercase', color: '#d97706',
            margin: '0 0 16px',
          }}>
            Select Machines to Rent <span style={{ color: '#c95a5a' }}>*</span>
          </h3>

          {errors.machines && <p style={{ ...errorMsg, marginBottom: 16 }}>{errors.machines}</p>}

          {/* Available Machines Dropdown */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Available Machines</label>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleAddMachine(e.target.value);
                  e.target.value = '';
                }
              }}
              style={{ ...inputStyle, opacity: serialsLoading ? 0.5 : 1 }}
              disabled={serialsLoading}
            >
              <option value="">Select a machine to add...</option>
              {serialNumbers?.map((sn: SerialNumber) => (
                <option
                  key={sn.id}
                  value={sn.id}
                  disabled={selectedMachines.some(m => m.serialNumberId === sn.id)}
                >
                  {sn.serialNumber} - {sn.product?.name} ({sn.warehouse?.name})
                </option>
              ))}
            </select>
          </div>

          {/* Selected Machines List */}
          {selectedMachines.length > 0 && (
            <div style={{
              background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)',
              borderRadius: 6, padding: 16,
            }}>
              <div style={{
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
                letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c',
                marginBottom: 12,
              }}>
                Selected Machines ({selectedMachines.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedMachines.map((machine) => (
                  <div
                    key={machine.serialNumberId}
                    style={{
                      background: '#ffffff', border: '1px solid rgba(92,78,51,0.2)',
                      borderRadius: 6, padding: 16,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Machine info + fee + remove */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, color: '#1c2033', margin: '0 0 2px' }}>
                            {machine.serialNumber}
                          </p>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: 0 }}>
                            {machine.productName}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 140 }}>
                            <label style={labelSm}>Monthly Fee (BD)</label>
                            <input
                              type="number" step="0.01" min="0"
                              value={machine.monthlyRentalFee}
                              onChange={(e) => handleMachinePriceChange(machine.serialNumberId, e.target.value)}
                              style={inputSm}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveMachine(machine.serialNumberId)}
                            style={{ ...dangerBtn, marginTop: 20 }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Bottle Tracking */}
                      {!isEditing && (
                        <div style={{ borderTop: '1px solid rgba(92,78,51,0.15)', paddingTop: 12 }}>
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 10 }}>
                            Water Bottles (Optional)
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                            <div>
                              <label style={labelSm}>Quantity</label>
                              <input
                                type="number" min="0"
                                value={machine.initialBottlesQty}
                                onChange={(e) => handleBottleChange(machine.serialNumberId, 'initialBottlesQty', e.target.value)}
                                placeholder="0"
                                style={inputSm}
                              />
                            </div>
                            <div>
                              <label style={labelSm}>Size (ML)</label>
                              <input
                                type="number" min="0"
                                value={machine.initialBottlesMl}
                                onChange={(e) => handleBottleChange(machine.serialNumberId, 'initialBottlesMl', e.target.value)}
                                placeholder="500"
                                style={inputSm}
                              />
                            </div>
                            <div>
                              <label style={labelSm}>Product</label>
                              <select
                                value={machine.bottlesProductId}
                                onChange={(e) => handleBottleChange(machine.serialNumberId, 'bottlesProductId', e.target.value)}
                                style={inputSm}
                              >
                                <option value="">Select...</option>
                                {bottleProducts.map((product: any) => (
                                  <option key={product.id} value={product.id}>{product.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        {selectedMachines.length > 0 && (
          <div style={{
            marginTop: 24,
            background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
            borderRadius: 6, padding: '16px 20px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 8px' }}>
                  Total Monthly Rental Fee
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedMachines.map((machine) => (
                    <p key={machine.serialNumberId} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: 0 }}>
                      {machine.serialNumber}: BD {machine.monthlyRentalFee.toFixed(2)}
                    </p>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', margin: '0 0 4px' }}>
                  {selectedMachines.length} machine{selectedMachines.length > 1 ? 's' : ''}
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#fbbf24', margin: 0 }}>
                  BD {totalMonthlyFee.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
        <button type="submit" style={primaryBtn}>
          {isEditing ? 'Update Contract' : 'Create Contract'}
        </button>
      </div>
    </form>
  );
};
